/**
 * Discharge Readiness Checks
 *
 * This module contains all functions that determine if various components
 * are ready for discharge operations.
 */

import type { IBatchStore } from '../batch-store-types';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { DeviceTypes } from '../../../constants/DeviceModels';
import { MixerType } from '../../../constants/BatchTypeEnums';
import logger from '../../../utils/logger';
import { Device } from '../../../../types/api/Device';

/**
 * Check if data is ready for final discharge
 * Verifies all scales and pumps are in the correct state before discharging
 *
 * @param batchStore - The batch store instance
 * @param runningData - The current batch data being processed
 * @returns true if ready for discharge, false otherwise
 */
export function isReadyForFinalDischarge(batchStore: IBatchStore, runningData: any): boolean {
  let ready = true;
  const sequence = runningData.sequence;

  if (runningData?.finalScales) {
    Object.keys(runningData.finalScales).forEach(scaleId => {
      const numericScaleId = Number(scaleId);
      const scale = runningData.finalScales[scaleId];
      const ds = batchStore.getDeviceStatus(numericScaleId);
      let dischargeScale = { ...sequence.discharges.find((v: any) => v.scaleId === numericScaleId)};

      if (runningData.uid !== ds.uid || runningData.currentDrop !== ds.currentDrop) {
        if (!dischargeScale.noWait && !runningData.finalScalesDischarged.includes(numericScaleId)) {
          logger.log(`Scale #${numericScaleId} ${dischargeScale.type} not ready for final discharge!`, ds);
          ready = false;
        }
      }

      if (ds.status !== DeviceStatus.WAIT_DISCH && !dischargeScale.noWait && !runningData.finalScalesDischarged.includes(numericScaleId)) {
        logger.log(`Scale #${numericScaleId} ${dischargeScale.type} not ready for final discharge! `, ds);
        ready = false;
      }

      if (dischargeScale.noWait && !batchStore.waitingForDischarge.some((v: any) => v.scaleId === numericScaleId)) {
        if (!runningData.finalScalesDischarged.includes(numericScaleId)) {
          logger.log(`Scale #${numericScaleId} ${dischargeScale.type} not ready for final discharge! `, batchStore.waitingForDischarge.length, runningData.finalScalesDischarged.length);
          ready = false;
        }
      }

      if (batchStore.isInputPortOff(scale.interlockStatusPNo)) {
        logger.log(`Scale #${numericScaleId} interlockStatusPNo ${scale.interlockStatusPNo} is off`);
        ready = false;
      }
    });

    let scalesWithSameData = 0;

    const keys = batchStore.deviceStatus.keys();
    // @ts-ignore
    for (let ds of batchStore.deviceStatus.values()) {
      const key = keys.next().value; // check noWait!!
      if (runningData.uid === ds.uid && runningData.currentDrop === ds.currentDrop
        && ds.type === DeviceTypes.Scale.toUpperCase() && Number.isFinite(Number(key)) && ds.status !== DeviceStatus.FINISH) {
        scalesWithSameData += 1;
      }
    }

    if (runningData.finalScales && Object.keys(runningData.finalScales).length !== (scalesWithSameData + runningData.finalScalesDischarged.length)) {// check noWait
      logger.log('Scales not ready for final discharge!', runningData.uid, runningData.currentDrop,
        Object.keys(runningData.finalScales).length, scalesWithSameData, runningData.finalScalesDischarged.length);
      ready = false;
    }
  }

  if (runningData?.pumps) {
    Object.keys(runningData.pumps).forEach(pumpId => {
      const pump = batchStore.devicesStore.getDeviceById(Number(pumpId));
      const ds = batchStore.getDeviceStatus(pump.id);
      if (ds.status !== DeviceStatus.IDLE && ds.status !== DeviceStatus.WAIT_DISCH && ds.status !== DeviceStatus.FINISH
        && ds.uid !== runningData.uid && ds.currentDrop !== runningData.currentDrop) {
        logger.log(`Pump #${pumpId} not ready for final discharge!`, ds);
        ready = false;
      }
    });
  }

  return ready;
}

/**
 * Check if mixer is ready for discharge
 * Verifies mixer state, position, and mode before allowing discharge
 *
 * @param batchStore - The batch store instance
 * @param runningData - The current batch data (optional)
 * @returns true if mixer is ready, false otherwise
 */
export function mixerIsReadyForDischarge(batchStore: IBatchStore, runningData: any = {}): boolean {
  const mixer = batchStore.devicesStore.getDeviceById(Number(runningData.mixerId));

  if (!mixer || runningData.isWashout || batchStore.isTestMode) {
    return true;
  }

  const mixerStatus = batchStore.getDeviceStatus(mixer.id);

  if (mixerStatus.status !== DeviceStatus.IDLE) {
    if (
      mixerStatus.uid !== runningData.uid
      || (!mixer.multibatch && mixerStatus.currentDrop !== runningData.currentDrop)
    ) {
      logger.log(`Mixer #${mixer.id} ${mixer.description || ''} not ready!`, mixerStatus);
      return false;
    }
  }

  if (mixer.type === MixerType.ForwardReverse) {
    if (batchStore.isInputPortOff(mixer.closeReturnStatusPNo) || !batchStore.isInputPortOff(mixer.openTiltStatusPNo) || batchStore.isOutputOn(mixer.openTiltPNo)) {
      batchStore.showWarning({ key: 'Mixer must be running in reverse mode!', props: { mixerId: mixer.id, mixerName: mixer.description || '' }});
      return false;
    }

    if (mixer.type === MixerType.ForwardReverse && !isMixerDischargeAllowed(batchStore, mixer)) {
      batchStore.showWarning({ key: 'Mixer is waiting on Analog Switch',
        props: {
          mixerId: mixer.id,
          mixerName: mixer.description || '',
          allowDischargeAt: mixer.allowDischargeAt || 0,
        }});
      return false;
    }
  } else {
    if (!batchStore.isMixerOn(mixer) && mixer.type !== MixerType.HrmAstec) {
      batchStore.showWarning({ key: 'Mixer must be running!', props: { mixerId: mixer.id, mixerName: mixer.description || '' }});
      return false;
    }
    if (batchStore.isInputPortOff(mixer.closeReturnStatusPNo) || !batchStore.isInputPortOff(mixer.openTiltStatusPNo)) {
      batchStore.showWarning({ key: 'Mixer must be closed!', props: { mixerId: mixer.id, mixerName: mixer.description || '' }});
      return false;
    }
  }

  return true;
}

/**
 * Check if mixer discharge is allowed based on analog switch value
 *
 * @param batchStore - The batch store instance
 * @param mixer - The mixer device
 * @returns true if discharge is allowed, false otherwise
 */
export function isMixerDischargeAllowed(batchStore: IBatchStore, mixer: any): boolean {
  if (!mixer?.analogSwitchId) {
    return true;
  }

  const analogSwitch = batchStore.rootStore.devicesStore.analogSwitches.find((v: any) => v.id === mixer.analogSwitchId);
  if (!analogSwitch) {
    return true;
  }

  const value = batchStore.analogOutputs[analogSwitch.channelPNo - 1];
  logger.log('is mixer discharge allowed:', !mixer.allowDischargeAt || value >= mixer.allowDischargeAt, mixer.allowDischargeAt, value);

  return !mixer.allowDischargeAt || value >= mixer.allowDischargeAt;
}

/**
 * Check if boot device is ready for discharge
 * Verifies boot device down status input
 *
 * @param batchStore - The batch store instance
 * @returns true if boot is ready or not present, false otherwise
 */
export function isBootDeviceReadyForDischarge(batchStore: IBatchStore): boolean {
  const boots = batchStore.devicesStore.boots;

  if (boots.length) {
    const boot = boots[0];
    if (boot && boot.downStatusPNo > 0 && batchStore.isInputPortOff(boot.downStatusPNo)) {
      batchStore.showError({ key: 'Can not discharge because Boot device down status input is off!', props: { bootId: boot.id }});
      return false;
    }
    return true;
  }

  return true;
}

/**
 * Check if we should wait on other scale discharge with the same data
 * Prevents simultaneous discharge of dependent scales
 *
 * @param batchStore - The batch store instance
 * @param scale - The scale to check
 * @returns true if should wait, false otherwise
 */
export function shouldWaitOnOtherScaleDischarge(batchStore: IBatchStore, scale: Device): boolean {
  const scaleStatus = batchStore.getDeviceStatus(scale.id);
  const otherScales = batchStore.devicesStore.devices.filter((v: any) => v.parentScaleId === scale.id);
  let waiting = false;

  otherScales.forEach((otherScale: Device) => {
    const otherScaleStatus = batchStore.getDeviceStatus(otherScale.id);

    if (otherScaleStatus.uid === scaleStatus.uid && otherScaleStatus.currentDrop === scaleStatus.currentDrop) {
      waiting = true;
    }
  });

  return waiting;
}

/**
 * Display notification that load is ready for discharge
 * Shows repeating info message until discharge starts
 *
 * @param batchStore - The batch store instance
 * @param initialData - The initial batch data
 */
export function showReadyForDischarge(batchStore: IBatchStore, initialData: any): void {
  if (!batchStore.running.length || !batchStore.isActiveDrop(initialData) || batchStore.discharging || batchStore.isDischarge) {
    return;
  }

  const displayTime = 3000;
  const timerName = 'timer-show-discharge-ready';
  batchStore.clearTimeout((batchStore as any)[timerName]);

  logger.log(`Load ${initialData.uid} ready for discharge!`, initialData);

  batchStore.showInfo({
    key: 'Load ready for discharge!',
    props: { loadId: initialData.tId || initialData.uid },
  }, displayTime);

  if (!batchStore.settingsStore.notificationSettings.discharge) {
    return;
  }

  (batchStore as any)[timerName] = batchStore.setTimeout(() => {
    showReadyForDischarge(batchStore, initialData);
  }, displayTime);
}
